// Pure D3/SVG implementations of Impact Path node designs
// Mimics the React components from ImpactPathNodes.stories.jsx

const categoryColors = {
  'climate': '#FF6B6B',
  'agriculture': '#4ECDC4',
  'policy': '#45B7D1',
  'technology': '#96CEB4',
  'economy': '#FECA57',
  'social': '#26D0CE',
  'education': '#A55EEA',
  'health': '#FF6EC7'
};

// ============= PATHWAY CARD (Regular Node) =============
export const renderPathwayCard = (selection, data) => {
  const CARD_WIDTH = 240;
  const CARD_HEIGHT = 100;
  const color = categoryColors[data.category] || data.color || '#007aff';
  
  const cardGroup = selection.append('g')
    .attr('class', 'pathway-card')
    .style('cursor', 'pointer');
  
  // Create gradient definition
  const defs = selection.append('defs');
  const gradientId = `pathway-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const gradient = defs.append('linearGradient')
    .attr('id', gradientId)
    .attr('x1', '0%').attr('y1', '0%')
    .attr('x2', '100%').attr('y2', '100%');
  
  gradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', color)
    .attr('stop-opacity', 0.87);
  
  gradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', color)
    .attr('stop-opacity', 0.6);
  
  // Shadow filter
  const filterId = `shadow-${Math.random().toString(36).substr(2, 9)}`;
  const filter = defs.append('filter')
    .attr('id', filterId)
    .attr('x', '-50%').attr('y', '-50%')
    .attr('width', '200%').attr('height', '200%');
  
  filter.append('feGaussianBlur')
    .attr('in', 'SourceAlpha')
    .attr('stdDeviation', 5);
  
  filter.append('feOffset')
    .attr('dx', 0)
    .attr('dy', 4)
    .attr('result', 'offsetblur');
  
  filter.append('feFlood')
    .attr('flood-color', '#000000')
    .attr('flood-opacity', 0.15);
  
  filter.append('feComposite')
    .attr('in2', 'offsetblur')
    .attr('operator', 'in');
  
  const feMerge = filter.append('feMerge');
  feMerge.append('feMergeNode');
  feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  
  // Glow effect
  const glowId = `glow-${Math.random().toString(36).substr(2, 9)}`;
  const glowFilter = defs.append('filter')
    .attr('id', glowId)
    .attr('x', '-50%').attr('y', '-50%')
    .attr('width', '200%').attr('height', '200%');
  
  glowFilter.append('feGaussianBlur')
    .attr('stdDeviation', 10)
    .attr('result', 'coloredBlur');
  
  const glowMerge = glowFilter.append('feMerge');
  glowMerge.append('feMergeNode').attr('in', 'coloredBlur');
  glowMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  
  // Main card background
  const card = cardGroup.append('rect')
    .attr('x', -CARD_WIDTH / 2)
    .attr('y', -CARD_HEIGHT / 2)
    .attr('width', CARD_WIDTH)
    .attr('height', CARD_HEIGHT)
    .attr('rx', 16)
    .attr('fill', `url(#${gradientId})`)
    // Impact Path v2: when the edge INTO this node carries a chosen effect
    // type, the card wears that colour — the relation and the thing it lands on
    // read as one statement. Untyped nodes keep the neutral border.
    .attr('stroke', data.borderColor || 'rgba(255, 255, 255, 0.2)')
    .attr('stroke-width', data.borderColor ? 2.5 : 2)
    .style('filter', `url(#${filterId})`);
  
  // Glossy top overlay
  const overlayGradientId = `overlay-${Math.random().toString(36).substr(2, 9)}`;
  const overlayGradient = defs.append('linearGradient')
    .attr('id', overlayGradientId)
    .attr('x1', '0%').attr('y1', '0%')
    .attr('x2', '0%').attr('y2', '100%');
  
  overlayGradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', 'white')
    .attr('stop-opacity', 0.15);
  
  overlayGradient.append('stop')
    .attr('offset', '50%')
    .attr('stop-color', 'white')
    .attr('stop-opacity', 0);
  
  cardGroup.append('rect')
    .attr('x', -CARD_WIDTH / 2)
    .attr('y', -CARD_HEIGHT / 2)
    .attr('width', CARD_WIDTH)
    .attr('height', CARD_HEIGHT / 2)
    .attr('rx', 16)
    .attr('fill', `url(#${overlayGradientId})`)
    .style('pointer-events', 'none');
  
  // Title text with proper wrapping
  const title = data.name || 'Untitled';
  const maxWidth = CARD_WIDTH - 40; // Leave padding
  
  const titleText = cardGroup.append('text')
    .attr('x', 0)
    .attr('y', 0)
    .attr('text-anchor', 'middle')
    .attr('fill', 'white')
    .attr('font-size', '14px')
    .attr('font-weight', '600')
    .style('text-shadow', '0 2px 8px rgba(0, 0, 0, 0.3)');
  
  // Wrap text function
  const wrapText = (text, width) => {
    const words = title.split(/\s+/);
    let line = '';
    let lineNumber = 0;
    const lineHeight = 18;
    const maxLines = 3;
    
    words.forEach((word, i) => {
      const testLine = line + (line ? ' ' : '') + word;
      const testText = titleText.append('tspan')
        .attr('x', 0)
        .attr('dy', 0)
        .text(testLine);
      
      const testWidth = testText.node().getComputedTextLength();
      testText.remove();
      
      if (testWidth > width && line) {
        // Current line is full, add it
        if (lineNumber < maxLines) {
          titleText.append('tspan')
            .attr('x', 0)
            .attr('dy', lineNumber === 0 ? -lineHeight/2 : lineHeight)
            .text(line);
          line = word;
          lineNumber++;
        }
      } else {
        line = testLine;
      }
      
      // Add last line (no ellipsis - title should already be properly sized)
      if (i === words.length - 1 && lineNumber < maxLines) {
        titleText.append('tspan')
          .attr('x', 0)
          .attr('dy', lineNumber === 0 ? 0 : lineHeight)
          .text(line);
      }
    });
  };
  
  wrapText(title, maxWidth);
  
  // Tags container - both category and sentiment
  const tagsGroup = cardGroup.append('g')
    .attr('transform', `translate(${-CARD_WIDTH / 2 + 20}, ${CARD_HEIGHT / 2 - 20})`);
  
  let tagX = 0;
  
  // Category badge
  if (data.category) {
    const categoryBadge = tagsGroup.append('g')
      .attr('transform', `translate(${tagX}, 0)`);
    
    const categoryWidth = data.category.length * 7 + 16;
    
    categoryBadge.append('rect')
      .attr('x', 0)
      .attr('y', -8)
      .attr('width', categoryWidth)
      .attr('height', 18)
      .attr('rx', 9)
      .attr('fill', 'rgba(0, 0, 0, 0.2)');
    
    categoryBadge.append('text')
      .attr('x', 8)
      .attr('y', 3)
      .attr('fill', 'rgba(255, 255, 255, 0.7)')
      .attr('font-size', '10px')
      .attr('font-weight', '500')
      .style('text-transform', 'uppercase')
      .style('letter-spacing', '0.5px')
      .text(data.category);
    
    tagX += categoryWidth + 6; // Space for next badge
  }
  
  // Sentiment badge
  if (data.sentiment) {
    const sentimentBadge = tagsGroup.append('g')
      .attr('transform', `translate(${tagX}, 0)`);
    
    // Map sentiment to display text and colors
    const sentimentConfig = {
      'positive': { text: 'Accelerator', color: 'rgba(76, 175, 80, 0.3)', icon: '⚡' },
      'negative': { text: 'Blocker', color: 'rgba(244, 67, 54, 0.3)', icon: '⛔' },
      'question': { text: 'Question', color: 'rgba(255, 152, 0, 0.3)', icon: '❓' },
      'neutral': { text: 'Neutral', color: 'rgba(158, 158, 158, 0.3)', icon: '◆' }
    };
    
    const config = sentimentConfig[data.sentiment] || sentimentConfig.neutral;
    const sentimentWidth = config.text.length * 6 + 20;
    
    sentimentBadge.append('rect')
      .attr('x', 0)
      .attr('y', -8)
      .attr('width', sentimentWidth)
      .attr('height', 18)
      .attr('rx', 9)
      .attr('fill', config.color);
    
    sentimentBadge.append('text')
      .attr('x', 8)
      .attr('y', 3)
      .attr('fill', 'rgba(255, 255, 255, 0.8)')
      .attr('font-size', '10px')
      .attr('font-weight', '600')
      .style('text-transform', 'uppercase')
      .style('letter-spacing', '0.3px')
      .text(config.text);
  }
  
  // Removed depth indicator for cleaner design
  
  // Hover effects
  cardGroup
    .on('mouseover', function() {
      card.transition().duration(300)
        .attr('stroke', 'rgba(255, 255, 255, 0.4)')
        .style('filter', `url(#${glowId})`);
      
      cardGroup.transition().duration(300)
        .attr('transform', 'translate(0, -4) scale(1.02)');
    })
    .on('mouseout', function() {
      card.transition().duration(300)
        .attr('stroke', 'rgba(255, 255, 255, 0.2)')
        .style('filter', `url(#${filterId})`);
      
      cardGroup.transition().duration(300)
        .attr('transform', 'translate(0, 0) scale(1)');
    });
  
  return cardGroup;
};

// ============= START NODE =============
export const renderStartNode = (selection, data) => {
  const CARD_WIDTH = 240;
  const CARD_HEIGHT = 100;
  
  const startGroup = selection.append('g')
    .attr('class', 'start-node')
    .style('cursor', 'pointer');
  
  // Gradient definition
  const defs = selection.append('defs');
  const gradientId = `start-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const gradient = defs.append('linearGradient')
    .attr('id', gradientId)
    .attr('x1', '0%').attr('y1', '0%')
    .attr('x2', '100%').attr('y2', '100%');
  
  gradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', '#4CAF50');
  
  gradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', '#2E7D32');
  
  // Shadow filter
  const shadowId = `start-shadow-${Math.random().toString(36).substr(2, 9)}`;
  const shadowFilter = defs.append('filter')
    .attr('id', shadowId)
    .attr('x', '-50%').attr('y', '-50%')
    .attr('width', '200%').attr('height', '200%');
  
  shadowFilter.append('feGaussianBlur')
    .attr('in', 'SourceAlpha')
    .attr('stdDeviation', 5);
  
  shadowFilter.append('feOffset')
    .attr('dx', 0)
    .attr('dy', 4)
    .attr('result', 'offsetblur');
  
  shadowFilter.append('feFlood')
    .attr('flood-color', '#4CAF50')
    .attr('flood-opacity', 0.3);
  
  shadowFilter.append('feComposite')
    .attr('in2', 'offsetblur')
    .attr('operator', 'in');
  
  const feMerge = shadowFilter.append('feMerge');
  feMerge.append('feMergeNode');
  feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  
  // Main card background
  const card = startGroup.append('rect')
    .attr('x', -CARD_WIDTH / 2)
    .attr('y', -CARD_HEIGHT / 2)
    .attr('width', CARD_WIDTH)
    .attr('height', CARD_HEIGHT)
    .attr('rx', 16)
    .attr('fill', `url(#${gradientId})`)
    .attr('stroke', 'rgba(255, 255, 255, 0.3)')
    .attr('stroke-width', 2)
    .style('filter', `url(#${shadowId})`);
  
  // Glossy overlay
  const overlayGradientId = `start-overlay-${Math.random().toString(36).substr(2, 9)}`;
  const overlayGradient = defs.append('linearGradient')
    .attr('id', overlayGradientId)
    .attr('x1', '0%').attr('y1', '0%')
    .attr('x2', '0%').attr('y2', '100%');
  
  overlayGradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', 'white')
    .attr('stop-opacity', 0.2);
  
  overlayGradient.append('stop')
    .attr('offset', '50%')
    .attr('stop-color', 'white')
    .attr('stop-opacity', 0);
  
  startGroup.append('rect')
    .attr('x', -CARD_WIDTH / 2)
    .attr('y', -CARD_HEIGHT / 2)
    .attr('width', CARD_WIDTH)
    .attr('height', CARD_HEIGHT / 2)
    .attr('rx', 16)
    .attr('fill', `url(#${overlayGradientId})`)
    .style('pointer-events', 'none');
  
  // Icon in top left corner
  startGroup.append('text')
    .attr('x', -CARD_WIDTH / 2 + 25)
    .attr('y', -CARD_HEIGHT / 2 + 35)
    .attr('font-size', '28px')
    .text(data.icon || '🌱');
  
  // "SKENAARIO" text after icon
  startGroup.append('text')
    .attr('x', -CARD_WIDTH / 2 + 55)
    .attr('y', -CARD_HEIGHT / 2 + 35)
    .attr('fill', 'white')
    .attr('font-size', '11px')
    .attr('font-weight', '600')
    .style('text-transform', 'uppercase')
    .style('letter-spacing', '0.3px')
    .style('opacity', '0.8')
    .text((data.scenarioLabel || 'SCENARIO').toUpperCase());
  
  // Label text (centered) - the actual situation
  const labelText = data.label || 'Current Situation';
  startGroup.append('text')
    .attr('x', 0)
    .attr('y', 5)
    .attr('text-anchor', 'middle')
    .attr('fill', 'white')
    .attr('font-size', '15px')
    .attr('font-weight', '600')
    .style('text-shadow', '0 2px 10px rgba(0, 0, 0, 0.4)')
    .text(labelText);
  
  // Hover effect
  startGroup
    .on('mouseover', function() {
      card.transition().duration(300)
        .attr('stroke', 'rgba(255, 255, 255, 0.5)');
      
      startGroup.transition().duration(300)
        .attr('transform', 'translate(0, -4) scale(1.02)');
    })
    .on('mouseout', function() {
      card.transition().duration(300)
        .attr('stroke', 'rgba(255, 255, 255, 0.3)');
      
      startGroup.transition().duration(300)
        .attr('transform', 'translate(0, 0) scale(1)');
    });
  
  return startGroup;
};

// ============= GOAL NODE =============
export const renderGoalNode = (selection, data) => {
  const CARD_WIDTH = 240;
  const CARD_HEIGHT = 100;
  
  const goalGroup = selection.append('g')
    .attr('class', 'goal-node')
    .style('cursor', 'pointer');
  
  const defs = selection.append('defs');
  
  // Gradient definition for card
  const gradientId = `goal-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const gradient = defs.append('linearGradient')
    .attr('id', gradientId)
    .attr('x1', '0%').attr('y1', '0%')
    .attr('x2', '100%').attr('y2', '100%');
  
  gradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', '#FFD700');
  
  gradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', '#FFA500');
  
  // Shadow filter with golden glow
  const shadowId = `goal-shadow-${Math.random().toString(36).substr(2, 9)}`;
  const shadowFilter = defs.append('filter')
    .attr('id', shadowId)
    .attr('x', '-50%').attr('y', '-50%')
    .attr('width', '200%').attr('height', '200%');
  
  shadowFilter.append('feGaussianBlur')
    .attr('in', 'SourceAlpha')
    .attr('stdDeviation', 5);
  
  shadowFilter.append('feOffset')
    .attr('dx', 0)
    .attr('dy', 4)
    .attr('result', 'offsetblur');
  
  shadowFilter.append('feFlood')
    .attr('flood-color', '#FFD700')
    .attr('flood-opacity', 0.3);
  
  shadowFilter.append('feComposite')
    .attr('in2', 'offsetblur')
    .attr('operator', 'in');
  
  const feMerge = shadowFilter.append('feMerge');
  feMerge.append('feMergeNode');
  feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  
  // Main card background
  const card = goalGroup.append('rect')
    .attr('x', -CARD_WIDTH / 2)
    .attr('y', -CARD_HEIGHT / 2)
    .attr('width', CARD_WIDTH)
    .attr('height', CARD_HEIGHT)
    .attr('rx', 16)
    .attr('fill', `url(#${gradientId})`)
    .attr('stroke', 'rgba(255, 255, 255, 0.4)')
    .attr('stroke-width', 2)
    .style('filter', `url(#${shadowId})`);
  
  // Glossy overlay
  const overlayGradientId = `goal-overlay-${Math.random().toString(36).substr(2, 9)}`;
  const overlayGradient = defs.append('linearGradient')
    .attr('id', overlayGradientId)
    .attr('x1', '0%').attr('y1', '0%')
    .attr('x2', '0%').attr('y2', '100%');
  
  overlayGradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', 'white')
    .attr('stop-opacity', 0.3);
  
  overlayGradient.append('stop')
    .attr('offset', '50%')
    .attr('stop-color', 'white')
    .attr('stop-opacity', 0);
  
  goalGroup.append('rect')
    .attr('x', -CARD_WIDTH / 2)
    .attr('y', -CARD_HEIGHT / 2)
    .attr('width', CARD_WIDTH)
    .attr('height', CARD_HEIGHT / 2)
    .attr('rx', 16)
    .attr('fill', `url(#${overlayGradientId})`)
    .style('pointer-events', 'none');
  
  // Animated star icon in top left
  const starSize = 20;
  const starX = -CARD_WIDTH / 2 + 25;
  const starY = -CARD_HEIGHT / 2 + 30;
  
  // Star gradient
  const starGradientId = `star-gradient-${Math.random().toString(36).substr(2, 9)}`;
  const starGradient = defs.append('linearGradient')
    .attr('id', starGradientId);
  
  starGradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', '#FFF');
  
  starGradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', '#FFE066');
  
  // Create rotating star icon
  const starGroup = goalGroup.append('g')
    .attr('transform', `translate(${starX}, ${starY})`);
  
  const starPath = generateStarPath(starSize, starSize * 0.5, 5);
  const star = starGroup.append('path')
    .attr('d', starPath)
    .attr('fill', `url(#${starGradientId})`)
    .style('filter', 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.6))');
  
  // Animate star rotation
  star.append('animateTransform')
    .attr('attributeName', 'transform')
    .attr('type', 'rotate')
    .attr('from', '0 0 0')
    .attr('to', '360 0 0')
    .attr('dur', '15s')
    .attr('repeatCount', 'indefinite');
  
  // "TARGET FUTURE" text after star icon
  goalGroup.append('text')
    .attr('x', starX + 30)
    .attr('y', starY)
    .attr('fill', '#333')
    .attr('font-size', '11px')
    .attr('font-weight', '600')
    .style('text-transform', 'uppercase')
    .style('letter-spacing', '0.3px')
    .style('opacity', '0.8')
    .text('TARGET FUTURE');
  
  // Label text (centered) - the actual goal
  const labelText = data.label || 'Desired Outcome';
  goalGroup.append('text')
    .attr('x', 0)
    .attr('y', 5)
    .attr('text-anchor', 'middle')
    .attr('fill', '#333')
    .attr('font-size', '15px')
    .attr('font-weight', '600')
    .style('text-shadow', '0 2px 6px rgba(0, 0, 0, 0.2)')
    .text(labelText);
  
  // Removed additional emoji icon for cleaner design
  
  // Hover effect
  goalGroup
    .on('mouseover', function() {
      card.transition().duration(300)
        .attr('stroke', 'rgba(255, 255, 255, 0.6)');
      
      goalGroup.transition().duration(300)
        .attr('transform', 'translate(0, -4) scale(1.02)');
    })
    .on('mouseout', function() {
      card.transition().duration(300)
        .attr('stroke', 'rgba(255, 255, 255, 0.4)');
      
      goalGroup.transition().duration(300)
        .attr('transform', 'translate(0, 0) scale(1)');
    });
  
  return goalGroup;
};

// Helper function to generate star path
function generateStarPath(outerRadius, innerRadius, points) {
  let path = '';
  const angle = Math.PI / points;
  
  for (let i = 0; i < 2 * points; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const x = Math.cos(i * angle - Math.PI / 2) * radius;
    const y = Math.sin(i * angle - Math.PI / 2) * radius;
    path += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
  }
  
  return path + ' Z';
}

// Export all renderers
export default {
  renderPathwayCard,
  renderStartNode,
  renderGoalNode,
  categoryColors
};